import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import nookies from 'nookies';
import Loader from 'components/LandingPage/Loader.jsx';
import PageMeta from 'components/PageMeta';
import ProductLayout from 'components/ProductLayout';
import logger from 'config/logger';
import { DEFAULT_LOCATION } from 'config/serverConfig';
import getContentData from 'data/contentstack/get-content-data';
import { getProductAndVariantsV2, getProductUrlsBySkus } from 'data/product-v2';
import universalHydration from 'data/universal-hydration';
import sspMiddleware from 'middleware/gssp';
import { RequestInstance } from 'request';
import formatStoreName from 'utils/format-store-name';
import getCanonicalUrl from 'utils/get-canonical-url';
import getCorrelationId from 'utils/get-correlation-id';
import isEmpty from 'utils/is-empty';
import { sendPageView, setMetricsData } from 'utils/metrics';

const getProductData = async ({ category, menu, slug, store, variantSKU }) => {
  let url = `api/products/product/${slug}?store=${formatStoreName(
    store
  )}&menu=${menu}&category=${category}`;
  if (variantSKU) url = `${url}&variant_sku=${variantSKU}`;
  const result = await RequestInstance.get(url);
  return result.products ?? [];
};

export default function ProductPage({
  allVariantsOOS,
  backInStockText,
  badgeImages,
  canonical,
  category,
  closeoutText,
  customSectionContent,
  defaultProductImage,
  defaultProductImageHeight,
  defaultProductImageWidth,
  effectsBanners,
  featuredProductText,
  groupedDiscountsContent,
  packagedDateTooltipInfo,
  location,
  newProductText,
  menu,
  noStoreSelected,
  packagedDateConfiguration,
  priceDropText,
  priceLabel,
  productData: propProductData,
  salesTaxDisclosure,
  slug,
  someVariantsOOS,
  storeClosedMessage,
  terpeneLabels,
  terpeneTooltipInfo,
  variantSKU,
}) {
  const [productData, setProductData] = useState(propProductData);
  const [storedStore, setStoredStore] = useState(location);
  const [storedMenu, setStoredMenu] = useState(menu);
  const [storeClosed, setStoreClosed] = useState(false);

  const selectedMenu = useSelector((state) => state.selectedMenu);
  const selectedStoreInformation = useSelector(
    (state) => state.selectedStoreInformation
  );
  const storeSelection = useSelector((state) => state.storeSelection);

  useEffect(() => {
    setMetricsData({
      location,
      menu,
      pageType: 'landing_page',
      title: productData.name,
    });
    sendPageView(productData.name);
  }, [productData.name, location, menu]);

  useEffect(() => {
    if (
      storeSelection &&
      selectedMenu &&
      (storedStore !== storeSelection || storedMenu !== selectedMenu)
    ) {
      getProductData({
        category,
        slug,
        variantSKU,
        store: storeSelection,
        menu: selectedMenu,
      })
        .then((data) => {
          setProductData(data);
          setStoredStore(storeSelection);
          setStoredMenu(selectedMenu);
        })
        .catch((err) => logger.error(err));
    }
  }, [
    category,
    selectedMenu,
    slug,
    storeSelection,
    storedMenu,
    storedStore,
    variantSKU,
  ]);

  useEffect(() => {
    if (propProductData !== productData) {
      setProductData(propProductData);
    }
  }, [propProductData]);

  useEffect(() => {
    const selectedStoreClosed = selectedStoreInformation?.store_closed ?? false;
    if (selectedStoreClosed !== storeClosed) {
      setStoreClosed(selectedStoreClosed);
    }
  }, [selectedStoreInformation]);

  const { component_list: components = [] } = customSectionContent || {};

  return (
    <>
      <PageMeta
        canonical={canonical}
        description={`Buy ${productData.name} Online at Lume Cannabis Co. `}
        title={`${productData.name} | Lume Cannabis Co.`}
      />
      <ProductLayout
        allVariantsOOS={allVariantsOOS}
        backInStockText={backInStockText}
        badgeImages={badgeImages}
        category={category}
        closeoutText={closeoutText}
        defaultProductImageHeight={defaultProductImageHeight}
        defaultProductImageWidth={defaultProductImageWidth}
        defaultProductImageUrl={defaultProductImage}
        effectsBanners={effectsBanners}
        featuredProductText={featuredProductText}
        groupedDiscountsContent={groupedDiscountsContent}
        packagedDateTooltipInfo={packagedDateTooltipInfo}
        newProductText={newProductText}
        noStoreSelected={noStoreSelected}
        packagedDateConfiguration={packagedDateConfiguration}
        priceDropText={priceDropText}
        priceLabel={priceLabel}
        productData={productData}
        salesTaxDisclosure={salesTaxDisclosure}
        selectedMenu={selectedMenu}
        someVariantsOOS={someVariantsOOS}
        storeClosed={storeClosed}
        storeClosedMessage={storeClosedMessage}
        storeSelection={storeSelection}
        terpeneLabels={terpeneLabels}
        terpeneTooltipInfo={terpeneTooltipInfo}
        variantSKU={variantSKU}
      />
      {components.map((component) => {
        const Component = Loader(component._content_type_uid);
        if (!Component) return null;
        return <Component key={component.uid} {...component} />;
      })}
    </>
  );
}

// eslint-disable-next-line complexity
export const getServerSideProps = sspMiddleware((store) => async (ctx) => {
  // path var is not used yet, but it probably will be used in the future
  const { category, slug } = ctx.params;
  const { variant_sku: variantSKU } = ctx.query;
  const { log } = ctx.req;
  const correlationId = getCorrelationId({ request: ctx.req });

  // if its an old URL that needs redirection...
  if (slug.includes('___')) {
    try {
      const variantSKU = slug.split('___')[1];
      const result = await getProductUrlsBySkus({
        correlationId,
        log,
        skus: [variantSKU],
      });

      if (!result || result.length === 0) {
        throw new Error(`Slug for ${variantSKU} not found`);
      }
      let { url } = result[0];
      if (!isEmpty(ctx.query)) url += `?variant_sku=${variantSKU}`;
      return {
        redirect: {
          destination: url,
          permanent: true,
        },
      };
    } catch (err) {
      log.error(err, 'Error redirecting to new product url');
    }
  }

  const { store: location = DEFAULT_LOCATION, menu = 'recreational' } =
    nookies.get(ctx);

  const categoryPath = `/shop/category/${category.replace('_', '-')}`;

  const [
    universal,
    productAndVariants,
    productDetailPageContent,
    shopPageCustomSection,
    groupedDiscounts,
  ] = await Promise.allSettled([
    universalHydration({ ctx, store, correlationId, log }),
    getProductAndVariantsV2({
      category,
      correlationId,
      log,
      productSlug: slug,
      variantSKU,
      store: formatStoreName(location),
      menu,
    }),
    getContentData({
      id: 'product_detail_page',
      params: {
        // eslint-disable-next-line camelcase
        include_branch: true,
        include: ['disclosure', 'product_labels', 'newsletter'],
      },
      correlationId,
      log,
    }),
    getContentData({
      id: 'shop_page_custom_sections',
      correlationId,
      log,
      params: {
        // eslint-disable-next-line camelcase
        include_branch: true,
        include: [
          'component_list',
          'component_list.items',
          'component_list.email_capture',
        ],
        query: `{"url": "${categoryPath}"}`,
      },
    }),
    getContentData({
      id: 'grouped_product_discounts',
      correlationId,
      log,
      params: {
        // eslint-disable-next-line camelcase
        group_id: 'tiered-flower',
      },
    }),
  ]);

  if (productAndVariants.status !== 'fulfilled' || !productAndVariants.value) {
    return {
      notFound: true,
    };
  }

  const safeProps = {
    allVariantsOOS:
      'This item is out of stock in this location, for availability, please select a different store.',
    backInStockText: 'BACK IN STOCK',
    badgeImages: [],
    closeoutText: 'LAST CALL',
    defaultCategoryImages: [],
    defaultProductImage: '',
    defaultProductImageHeight: 750,
    defaultProductImageWidth: 750,
    effectsBanners: [],
    location,
    menu,
    noStoreSelected: '',
    priceLabel: 'Price',
    salesTaxDisclosure: '*Cannabis and Sales tax will be added at checkout',
    someVariantsOOS:
      'This item is out of stock in this location, please choose a different item or select a different store.',
    storeClosedMessage:
      'This store location is temporarily closed. Please check back later or select a different store.',
    terpeneLabels: [],
  };

  if (productDetailPageContent.status === 'fulfilled') {
    const productDetailPageContentData =
      productDetailPageContent.value?.data?.[0] ?? null;

    const productLabels =
      productDetailPageContentData?.product_labels?.[0] ?? null;
    if (productLabels) {
      const {
        all_variants_oos: allVariantsOOS,
        back_in_stock_text: backInStockText,
        badge_images: badgeImages,
        closeout_text: closeoutText,
        default_category_images: defaultCategoryImages,
        effects_banners: effectsBanners,
        featured_product_text: featuredProductText,
        packaged_date_tooltip_info: packagedDateTooltipInfo,
        price_label: priceLabel,
        new_product_text: newProductText,
        no_store_selected: noStoreSelected,
        packaged_date_configuration: packagedDateConfiguration,
        price_drop_text: priceDropText,
        some_variants_oos: someVariantsOOS,
        store_closed: storeClosedMessage,
        terpenes: terpeneLabels,
        terpene_tooltip_info: terpeneTooltipInfo,
      } = productLabels;
      safeProps.allVariantsOOS = allVariantsOOS;
      safeProps.backInStockText = backInStockText;
      safeProps.badgeImages = badgeImages;
      safeProps.closeoutText = closeoutText;
      safeProps.defaultCategoryImages = defaultCategoryImages;
      safeProps.effectsBanners = effectsBanners;
      safeProps.featuredProductText = featuredProductText;
      safeProps.packagedDateConfiguration = packagedDateConfiguration;
      safeProps.packagedDateTooltipInfo = packagedDateTooltipInfo;
      safeProps.priceLabel = priceLabel;
      safeProps.newProductText = newProductText;
      safeProps.noStoreSelected = noStoreSelected;
      safeProps.priceDropText = priceDropText;
      safeProps.someVariantsOOS = someVariantsOOS;
      safeProps.storeClosedMessage = storeClosedMessage;
      safeProps.terpeneLabels = terpeneLabels;
      safeProps.terpeneTooltipInfo = terpeneTooltipInfo;
      const defaultForCategory = defaultCategoryImages.find(
        ({ title }) => title.toLowerCase() === category
      );
      const otherDefault = defaultCategoryImages.find(
        ({ title }) => title === 'Other'
      );
      if (defaultForCategory?.image?.url) {
        safeProps.defaultProductImage = defaultForCategory.image.url;
        safeProps.defaultProductImageHeight = defaultForCategory.image_height;
        safeProps.defaultProductImageWidth = defaultForCategory.image_width;
      } else {
        safeProps.defaultProductImage = otherDefault?.image?.url;
        safeProps.defaultProductImageHeight = otherDefault?.image_height;
        safeProps.defaultProductImageWidth = otherDefault?.image_width;
      }
    }

    const disclosure = productDetailPageContentData?.disclosure?.[0] ?? null;
    if (disclosure) {
      const { sales_tax_at_checkout: salesTaxDisclosure } = disclosure;
      safeProps.salesTaxDisclosure = salesTaxDisclosure;
    }
  }

  if (shopPageCustomSection.status === 'fulfilled') {
    safeProps.customSectionContent =
      shopPageCustomSection?.value?.data?.[0] ?? {};
  }

  const productData = productAndVariants.value;
  if (!productData) {
    return {
      notFound: true,
    };
  }

  if (productData.category !== category) {
    const queryParameter = variantSKU ? `?variant_sku=${variantSKU}` : '';
    const redirectToUrl = `${productData.url}${queryParameter}`;
    return {
      redirect: {
        destination: redirectToUrl,
        permanent: true,
      },
    };
  }

  if (productData.url) {
    safeProps.canonical = getCanonicalUrl(productData.url);
  }

  if (groupedDiscounts.status === 'fulfilled') {
    safeProps.groupedDiscountsContent =
      groupedDiscounts?.value?.data?.[0]?.discounts ?? [];
  }

  safeProps.metrics = {
    title: productData.name,
    pageType: 'product_page',
    location,
    menu,
  };

  return {
    props: {
      ...safeProps,
      category,
      footer: universal.value.footer,
      header: universal.value.header,
      productData,
      slug,
      variantSKU: variantSKU || null,
    },
  };
});
